import React, { useMemo } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { SettlementDialogProps } from '../types';

export function SettlementDialog({
  isDark,
  isOpen,
  onOpenChange,
  providers,
  formData,
  onFormDataChange,
  onSubmit,
  isEditing,
  isSubmitting,
  isBankConfirmed,
}: SettlementDialogProps) {
  const provider = useMemo(
    () => providers.find((p) => p.id === formData.providerId),
    [providers, formData.providerId],
  );
  const currency = provider?.currency || 'UZS';
  const needsRate = currency !== 'UZS';

  const inputCls = cn(
    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
  );
  const labelCls = cn(isDark ? 'text-slate-300' : 'text-slate-700');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-xl', isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
            {isEditing ? 'Изменить поступление' : 'Добавить поступление'}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {isBankConfirmed
              ? 'Подтверждено выпиской банка: сумму, дату и провайдера менять нельзя, только комментарий. Ошибку исправляют отдельным поступлением с обратным знаком.'
              : 'Деньги, реально зачисленные на счёт по отчёту провайдера. Возврат заводится отрицательной суммой.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Дата зачисления *</Label>
              <Input
                type="date"
                value={formData.date}
                disabled={isBankConfirmed}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, date: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Провайдер *</Label>
              <Select
                value={formData.providerId}
                disabled={isBankConfirmed}
                onValueChange={(value) =>
                  onFormDataChange((prev) => ({ ...prev, providerId: value }))
                }
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue placeholder="Выберите провайдера" />
                </SelectTrigger>
                <SelectContent className={cn(isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
                  {providers
                    .filter((provider) => provider.is_active)
                    .map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {provider.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className={cn('grid gap-4', needsRate ? 'grid-cols-2' : 'grid-cols-1')}>
            <div className="space-y-2">
              <Label className={labelCls}>Сумма, {currency} *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount || ''}
                disabled={isBankConfirmed}
                onChange={(e) =>
                  onFormDataChange((prev) => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))
                }
                className={inputCls}
              />
            </div>
            {needsRate && (
              <div className="space-y-2">
                <Label className={labelCls}>Курс {currency} → сум *</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={formData.rate || ''}
                  disabled={isBankConfirmed}
                  onChange={(e) =>
                    onFormDataChange((prev) => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))
                  }
                  className={inputCls}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>Комментарий</Label>
            <Input
              type="text"
              placeholder="Например: платёжка №123 за 11–17 августа"
              value={formData.comment || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, comment: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            Отмена
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
